import dayjs, { type Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import type { DateRangeType, PeriodGranularityType, TranslateType } from '../types/period.type';

dayjs.extend(isoWeek);
dayjs.extend(localizedFormat);

/**
 * Labels one period unit the way the granularity names it: a date, an ISO week or a month.
 */
export function formatUnit(date: Dayjs, granularity: PeriodGranularityType, t: TranslateType): string {
  switch (granularity) {
    case 'weekly':
      // Vue renders this value as text, so tell i18next not to HTML-escape it.
      return t('filters.period.week', {
        week: date.isoWeek(),
        year: date.isoWeekYear(),
        interpolation: { escapeValue: false }
      });
    case 'monthly':
      return date.format('MMMM YYYY');
    default:
      return date.format('L');
  }
}

/**
 * Labels a range as `start → end`, collapsed to a single unit when both bounds share it.
 */
export function formatRange(range: DateRangeType, granularity: PeriodGranularityType, t: TranslateType): string {
  const start = formatUnit(range.start, granularity, t);
  const end = formatUnit(range.end, granularity, t);

  if (start === end) {
    return start;
  }

  const wordingKey = granularity === 'daily' ? 'filters.period.range.days' : 'filters.period.range.units';

  // Vue renders this value as text, so tell i18next not to HTML-escape the dates' slashes.
  return t(wordingKey, { start, end, interpolation: { escapeValue: false } });
}
